<script setup lang="ts">
import Footer from '@/components/AppFooter.vue';
import HouseStageInfo from '@/components/HouseStageInfo.vue';
import TaskCard from '@/components/TaskCard.vue';
import blobToDataUrl from '@/composables/useFileReader';
import { useAppApi, type LumarActivityWeekDto } from '@/plugins/appApi';
import { useProjectStore } from '@/stores/project';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStorage } from '@vueuse/core';
import moment from 'moment';
import useLoading from '@/composables/useLoading';
import withRetry from '@/composables/withRetry';
import { useNetworkStore } from '@/stores/network';

interface RenderBase64Cache {
  base64: string;
  cachedAt: string;
}

const router = useRouter();

const loading = ref(false);
const currentPhase: Ref<number | undefined> = ref();
const loadingActivities = ref(false);
const heroImageSrc = ref('');
const projectStore = useProjectStore();
const houseModel = ref('');
const movingDate: Ref<Date | undefined> = ref(undefined);

const oneWeekAgo = new Date();
oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

const allActivities = ref<LumarActivityWeekDto[]>([]);
const currentActivities = computed<LumarActivityWeekDto[]>(() => {
  return allActivities.value.filter((a) => {
    return (
      moment(a.year.toString() + 'W' + a.week.toString().padStart(2, '0'))
        .toDate()
        .getTime() > oneWeekAgo.getTime()
    );
  });
});

const client = useAppApi();

const loadActivities = async () => {
  try {
    const response = await withRetry(() =>
      client.GET('/api/projects/{uid}/activities', {
        params: {
          path: {
            uid: projectStore.getProjectUid()
          }
        }
      })
    );
    allActivities.value = response.data!;
  } catch (e: unknown) {
    useNetworkStore().signalIssue(e);
  }
};

const loadState = async () => {
  const cachedBase64 = useStorage('render-base-64', {} as RenderBase64Cache);

  if (cachedBase64.value.base64) {
    heroImageSrc.value = cachedBase64.value.base64;
  }

  try {
    const { data: heroImage } = await client.GET('/api/projects/{uid}/render', {
      params: {
        path: {
          uid: projectStore.getProjectUid()
        }
      },
      parseAs: 'blob'
    });
    if (heroImage) {
      heroImageSrc.value = await blobToDataUrl(heroImage);
      cachedBase64.value.base64 = heroImageSrc.value;
      cachedBase64.value.cachedAt = new Date().toISOString();
    }

    const infoResponse = await client.GET('/api/projects/{uid}', {
      params: {
        path: {
          uid: projectStore.getProjectUid()
        }
      }
    });
    houseModel.value = infoResponse.data!.houseModel!;
    movingDate.value = new Date(infoResponse.data!.movingDate!);
    currentPhase.value = infoResponse.data!.phase!;
  } catch (e) {
    useNetworkStore().signalIssue(e);
  }
};

onMounted(() => {
  return Promise.all([
    useLoading(loading, loadState),
    useLoading(loadingActivities, loadActivities)
  ]);
});

function goToKnowledgeBase() {
  window.open('https://podpora.lumar.si/zivi_najbolje', '_blank')?.focus();
}
</script>

<template>
  <div>
    <div class="relative flex flex-col">
      <div v-if="!heroImageSrc" style="height: 466px; color: #afb2b8" class="w-full"></div>
      <img
        v-if="heroImageSrc"
        :src="heroImageSrc"
        alt="house-background"
        class="w-full min-h-[466px] max-h-[1024px] object-cover object-left"
        height="466"
      />
      <HouseStageInfo
        class="absolute bottom-0 p-4 md:px-40"
        :house-model
        :moving-date
        :loading-days="loading"
        :loading-phases="loading"
        :current-phase
      />
    </div>
    <div
      class="flex flex-col pt-6 px-4 md:px-40 min-[1300px]:px-40 pb-12 gap-2 min-[1300px]:gap-11"
    >
      <!-- <div class="flex-col gap-4 bg-lumar-light-gray p-4 rounded-lg hidden">
        <div class="flex items-center justify-between">
          <h1 class="text-2xl min-[1300px]:text-5xl text-lumar-blue font-semibold">Moje naloge</h1>
          <div style="cursor: pointer" @click="router.push('/my-tasks')">
            <span class="hidden min-[1300px]:inline-block align-text-bottom text-xl text-lumar-blue"
              >Prikaži več</span
            >
            <Button icon="pi pi-angle-right" variant="text" rounded aria-label="arrow right" />
          </div>
        </div>
        <div class="flex flex-col gap-1.5">
          <span class="text-lumar-dark-gray/70 text-xs min-[1300px]:text-xl"
            >Opravljene naloge</span
          >
          <span class="text-lumar-gray text-[20px] min-[1300px]:text-[28px] leading-6">17/20</span>
        </div>
        <div
          class="flex items-center overflow-auto gap-2 min-[1300px]:grid min-[1300px]:grid-cols-2 min-[1300px]:gap-4"
        >
          <TaskCard
            :activity="{ contractor: 'Test', date: '1.1.2025', status: 'Completed', type: 'Test' }"
          />
          <TaskCard
            :activity="{ contractor: 'Test', date: '1.1.2025', status: 'Completed', type: 'Test' }"
          />
          <TaskCard
            :activity="{ contractor: 'Test', date: '1.1.2025', status: 'Completed', type: 'Test' }"
          />
        </div>
      </div> -->
      <Skeleton
        height="192px"
        class="mb-2"
        border-radius="0.5rem"
        style="margin-bottom: 0"
        v-if="loadingActivities"
        :pt="{
          root: {
            style: {
              background: 'var(--color-lumar-light-gray)'
            }
          }
        }"
      ></Skeleton>
      <div
        class="flex flex-col gap-4 bg-lumar-light-gray p-4 rounded-lg"
        v-if="currentActivities.length && !loadingActivities"
      >
        <div class="flex items-center justify-between">
          <h1 class="text-2xl text-lumar-blue font-semibold">{{ $t('home.lumarActivities') }}</h1>
          <div style="cursor: pointer" @click="router.push('/lumar-activities')">
            <span
              class="hidden min-[1300px]:inline-block align-text-bottom text-xl text-lumar-blue"
              >{{ $t('home.showMore') }}</span
            >
            <Button icon="pi pi-angle-right" variant="text" rounded aria-label="arrow right" />
          </div>
        </div>
        <div
          class="flex items-center overflow-auto gap-2 min-[1300px]:grid min-[1300px]:grid-cols-2 min-[1300px]:gap-4"
          v-if="currentActivities.length"
        >
          <div v-for="activity in currentActivities" :key="activity.year + activity.week">
            <TaskCard :activity />
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-4 rounded-lg"></div>
      <div class="flex flex-col gap-4 bg-lumar-light-gray p-4 rounded-lg">
        <div class="flex items-center justify-between">
          <span class="text-base"> {{ $t('home.buildingAdvice') }} </span>
          <div style="cursor: pointer" @click="goToKnowledgeBase()">
            <span
              class="hidden min-[1300px]:inline-block align-text-bottom text-xl text-lumar-blue"
            ></span>
            <Button icon="pi pi-angle-right" variant="text" rounded aria-label="arrow right" />
          </div>
        </div>
      </div>
    </div>
    <div class="px-4 pb-6">
      <Footer />
    </div>
  </div>
</template>
